import { useEffect, useRef } from "react";

/**
 * Automatically paginates through all remaining pages of a test's `results`
 * connection using Apollo's `fetchMore`.
 *
 * The Apollo cache merge policy for `PESTest.results` appends incoming nodes
 * when `args.after` is set, so each `fetchMore` call grows the cached node
 * list. Once `hasNextPage` flips to `false` the effect stops.
 */
export function useFetchAllResults(
  fetchMore: (options: {
    variables: { resultsAfter: string };
  }) => Promise<unknown>,
  results:
    | {
        pageInfo?: {
          hasNextPage: boolean;
          endCursor?: string | null;
        } | null;
      }
    | null
    | undefined,
) {
  const fetchingRef = useRef(false);

  useEffect(() => {
    const pageInfo = results?.pageInfo;
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor || fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    fetchMore({ variables: { resultsAfter: pageInfo.endCursor } }).finally(
      () => {
        fetchingRef.current = false;
      },
    );
  }, [results?.pageInfo?.hasNextPage, results?.pageInfo?.endCursor, fetchMore]);
}
